import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import "@phosphor-icons/webcomponents/PhCamera";
import "@phosphor-icons/webcomponents/PhCheck";
import "@phosphor-icons/webcomponents/PhX";
import "@phosphor-icons/webcomponents/PhWarning";
import "ui-components/button/wow-button";
import "ui-components/form-elements/switch/switch";
import "ui-components/modal/wow-modal";

import { t } from "../../translations/i18n";
import { styles } from "./stream-config-modal.css";

type PermissionState = "prompt" | "granted" | "denied" | "checking";

@customElement("stream-config-modal")
export class StreamConfigModal extends LitElement {
  static override styles = [styles];

  @property({ type: String })
  streamId!: string;

  @state()
  private enableAudio = true;

  @state()
  private permissionState: PermissionState = "checking";

  override connectedCallback() {
    super.connectedCallback();
    void this.checkCameraPermission();
  }

  private async checkCameraPermission(): Promise<void> {
    try {
      const cameraPermission = await navigator.permissions.query({ name: "camera" as PermissionName });
      this.permissionState = cameraPermission.state as PermissionState;

      cameraPermission.addEventListener("change", () => {
        this.permissionState = cameraPermission.state as PermissionState;
      });
    } catch {
      this.permissionState = "prompt";
    }
  }

  private handleAudioChange(e: CustomEvent): void {
    this.enableAudio = e.detail.checked;
  }

  private handleCancel(): void {
    this.dispatchEvent(new CustomEvent("cancel", { bubbles: true, composed: true }));
  }

  private handleStart(): void {
    this.dispatchEvent(
      new CustomEvent("start-stream", {
        detail: {
          enableAudio: this.enableAudio,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleClose(): void {
    this.handleCancel();
  }

  private renderPermissionStatus() {
    const statusClasses = {
      "permission-status": true,
      [this.permissionState]: true,
    };

    switch (this.permissionState) {
      case "checking":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-camera size="20"></ph-camera>
            <span>${t("streamConfigModal.checkingPermissions")}</span>
          </div>
        `;
      case "granted":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-check size="20" weight="bold"></ph-check>
            <span>${t("streamConfigModal.cameraAccessGranted")}</span>
          </div>
        `;
      case "denied":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-x size="20" weight="bold"></ph-x>
            <span>${t("streamConfigModal.cameraAccessDenied")}</span>
          </div>
        `;
      case "prompt":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-warning size="20"></ph-warning>
            <span>${t("streamConfigModal.cameraPermissionPrompt")}</span>
          </div>
        `;
    }
  }

  override render() {
    return html`
      <wow-modal prevent-close @close=${this.handleClose}>
        <span slot="header">${t("streamConfigModal.header")}</span>

        <div class="config-content">
          <div class="section">
            <div class="audio-section">
              <span class="audio-label">${t("streamConfigModal.streamWithSound")}</span>
              <wow-switch .checked=${this.enableAudio} @change=${this.handleAudioChange}></wow-switch>
            </div>
          </div>

          <div class="section">${this.renderPermissionStatus()}</div>

          <div class="modal-actions">
            <wow-button variant="neutral" mobile-full-width @click=${this.handleCancel}
              >${t("streamConfigModal.cancel")}</wow-button
            >
            <wow-button
              variant="primary"
              mobile-full-width
              @click=${this.handleStart}
              ?disabled=${this.permissionState === "denied"}
            >
              ${t("streamConfigModal.startStreaming")}
            </wow-button>
          </div>
        </div>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "stream-config-modal": StreamConfigModal;
  }
}
